import { GetErgonomicWorkAnalysisResponseDTO } from '@/core/dto/ergonomic-work-analisys';
import type {
	ErgonomicWorkAnalysis,
	ErgonomicWorkAnalysisImage,
	KeyResultsMapped
} from '@/types/ErgonomicWorkAnalysis';

export class GetErgonomicWorkAnalysisMapper {
	constructor(private readonly data: GetErgonomicWorkAnalysisResponseDTO) {}

	transformKeyResults(): KeyResultsMapped | undefined {
		if (!this.data.ergonomic_work_analysis_key_results?.length) {
			return undefined;
		}

		const mappedKeyResults = this.data.ergonomic_work_analysis_key_results?.reduce((items, currentItem) => {
			const { custom_report_result_id, custom_report_step_id, step_key_id } = currentItem;

			if (!items[custom_report_result_id]) {
				items[custom_report_result_id] = {};
			}

			if (!items[custom_report_result_id][custom_report_step_id]) {
				items[custom_report_result_id][custom_report_step_id] = {
					selected_criteria: []
				};
			}

			items[custom_report_result_id][custom_report_step_id].selected_criteria.push(step_key_id);

			return items;
		}, {} as KeyResultsMapped);

		return mappedKeyResults;
	}

	getSignatures(): string[] | undefined {
		if (!this.data.ergonomic_work_analysis_signatures?.length) {
			return undefined;
		}

		return this.data.ergonomic_work_analysis_signatures?.map((signature) => signature.signature_responsible_id);
	}

	transformImages(): Record<string, ErgonomicWorkAnalysisImage> {
		const formattedImages: Record<string, ErgonomicWorkAnalysisImage> = {};
		this.data.images?.forEach((image) => {
			formattedImages[image.id] = {
				id: image.id,
				description: image.description || '',
				image_url: image.image_url,
				location: image.location
			};
		});
		return formattedImages;
	}

	toDomain(): ErgonomicWorkAnalysis {
		return {
			...this.data,

			working_hour_hidden: this.data.working_hour_hidden,
			working_hour: this.data.working_hour,
			working_hour_minutes: this.data.working_hour_minutes,

			location_description_hidden: this.data.location_description_hidden,
			location_description: this.data.location_description ?? undefined,

			planned_task_description_hidden: this.data.planned_task_description_hidden,
			planned_task_description: this.data.planned_task_description ?? undefined,

			performed_task_description_hidden: this.data.performed_task_description_hidden,
			performed_task_description: this.data.performed_task_description ?? undefined,

			working_population_hidden: this.data.working_population_hidden,
			working_population_men: this.data.working_population_men,
			working_population_women: this.data.working_population_women,
			working_population_others: this.data.working_population_others,

			working_conditions_hidden: this.data.working_conditions_hidden,
			working_conditions_description: this.data.working_conditions_description ?? undefined,

			working_verbalization_hidden: this.data.working_verbalization_hidden,
			working_verbalization_description: this.data.working_verbalization_description ?? undefined,

			worker_stress_level_assessment_hidden: this.data.worker_stress_level_assessment_hidden,
			worker_stress_level_assessment: this.data.worker_stress_level_assessment ?? undefined,

			signature_responsible_id: this.getSignatures(),

			ergonomic_work_analysis_key_results: this.transformKeyResults(),

			images: this.transformImages()
		};
	}
}
